/**
 * Learning from mistakes, and seeing progress (docs/SCHAAK.md §3.7). Pure:
 * the day log behind the rating graph and the streak, his own reasons for a
 * mistake, and the one thing to work on that both add up to.
 */
export const DAY_LOG_MAX = 365;
/** A local date as YYYY-MM-DD — the day the learner lives in, not UTC's. */
export function localDay(now) {
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    const d = String(now.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
}
/** Count one attempt on `day`, with the rating as it now stands. */
export function logAttempt(days, day, rating, solved) {
    const out = days.filter((x) => x.d !== day);
    const today = days.find((x) => x.d === day) ?? { d: day, r: rating, n: 0, s: 0 };
    out.push({ d: day, r: rating, n: today.n + 1, s: today.s + (solved ? 1 : 0) });
    return out.sort((a, b) => (a.d < b.d ? -1 : 1)).slice(-DAY_LOG_MAX);
}
function prevDay(day) {
    const [y, m, d] = day.split('-').map(Number);
    return localDay(new Date(y, m - 1, d - 1));
}
/** Days in a row with at least one attempt, ending today — or yesterday, so a streak survives until tonight. */
export function streakDays(days, today) {
    const active = new Set(days.filter((x) => x.n > 0).map((x) => x.d));
    let day = active.has(today) ? today : prevDay(today);
    let n = 0;
    while (active.has(day)) {
        n++;
        day = prevDay(day);
    }
    return n;
}
/* ---------------- his own reasons ---------------- */
export const MISTAKE_REASONS = ['threat', 'hanging', 'missed', 'calc', 'rushed', 'unsure'];
/** The choices, in his words, and the habit that answers each one. */
export const REASON_INFO = {
    threat: {
        label: 'Ik zag zijn dreiging niet',
        advice: 'Vraag na elke zet van de ander eerst: wat wil hij nu? Wat valt hij aan?',
    },
    hanging: {
        label: 'Ik zag niet dat mijn stuk hing',
        advice: 'Blunder-check vóór elke zet: welke stukken van mij staan onverdedigd na deze zet?',
    },
    missed: {
        label: 'Ik zag de betere zet niet',
        advice: 'Kijk eerst naar alle schaakjes, slagen en dreigingen — voor jou én de ander.',
    },
    calc: {
        label: 'Ik rekende verkeerd',
        advice: 'Reken de zet af tot het einde: wat doet hij terug, en wat doe ik dan?',
    },
    rushed: {
        label: 'Te snel gespeeld',
        advice: 'Speel langzamere partijen (15+10) en neem bij elke slagwisseling even de tijd.',
    },
    unsure: {
        label: 'Weet ik niet',
        advice: 'Speel de weerlegging na tot je ziet wat er gebeurt.',
    },
};
/* ---------------- what to work on ---------------- */
/** Which tactics theme trains against which kind of mistake. */
export const KIND_THEME = {
    hung: 'hangingPiece',
    missed_capture: 'hangingPiece',
    missed_mate: 'mateIn1',
    allowed_mate: 'backRankMate',
    other: 'fork',
};
/** Advice by kind, for when he has not said why. */
export const KIND_ADVICE = {
    hung: REASON_INFO.hanging.advice,
    missed_capture: 'Kijk bij elke zet van de ander: wat staat er nu onverdedigd? Dat mag jij slaan.',
    missed_mate: 'Begin elke zet met de schaakjes die je hebt — soms is er één die mat zet.',
    allowed_mate: 'Laat je koning niet opgesloten staan: een luchtgat (h3 of h6) voorkomt veel mat.',
    other: REASON_INFO.missed.advice,
};
/**
 * The one thing to work on: his most frequent kind of mistake over the last
 * `days` days (ties: the more serious kind first), with his most named reason
 * deciding the advice when he has given one. Null with no mistakes in the window.
 */
export function focusOf(facts, now, days = 30) {
    const since = new Date(now.getTime() - days * 86_400_000).toISOString();
    const recent = facts.filter((f) => f.played_at >= since);
    if (recent.length === 0)
        return null;
    const order = ['allowed_mate', 'hung', 'missed_mate', 'missed_capture', 'other'];
    const byKind = new Map();
    for (const f of recent)
        byKind.set(f.kind, (byKind.get(f.kind) ?? 0) + 1);
    const [kind, count] = [...byKind.entries()].sort((a, b) => b[1] - a[1] || order.indexOf(a[0]) - order.indexOf(b[0]))[0];
    const byReason = new Map();
    for (const f of recent)
        if (f.reason && f.reason !== 'unsure')
            byReason.set(f.reason, (byReason.get(f.reason) ?? 0) + 1);
    const reason = [...byReason.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null;
    return {
        kind,
        count,
        reason,
        theme: KIND_THEME[kind] ?? 'hangingPiece',
        advice: reason ? REASON_INFO[reason].advice : (KIND_ADVICE[kind] ?? REASON_INFO.missed.advice),
    };
}
/** A mistake counts as learned once it was solved again on a later review (box past the first solve's). */
export const LEARNED_BOX = 3;
export const isLearned = (c) => !!c && c.box >= LEARNED_BOX;
/** Cards due within the next `hours` — "morgen klaar". */
export function dueWithin(cards, now, hours) {
    const until = new Date(now.getTime() + hours * 3_600_000).toISOString();
    return Object.values(cards).filter((c) => c && c.kind !== 'drill' && c.due_at && c.due_at <= until).length;
}
//# sourceMappingURL=learning.js.map